"use client";

import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from 'react-i18next';

interface ErrorDialogContextType {
  showErrorDialog: (message: string, title?: string) => Promise<void>;
}

const ErrorDialogContext = createContext<ErrorDialogContextType | undefined>(undefined);

export const ErrorDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const resolveRef = React.useRef<(() => void) | null>(null);
  const { t } = useTranslation();

  const showErrorDialog = (message: string, title?: string) => {
    setTitle(title || t('common.errorTitle', { defaultValue: 'Error' }));
    setMessage(message);
    setIsOpen(true);
    return new Promise<void>(resolve => {
      resolveRef.current = resolve;
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current();
      resolveRef.current = null;
    }
  };

  return (
    <ErrorDialogContext.Provider value={{ showErrorDialog }}>
      {children}
      <AlertDialog open={isOpen} onOpenChange={open => (!open ? handleClose() : setIsOpen(open))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleClose}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ErrorDialogContext.Provider>
  );
};

export const useErrorDialog = () => {
  const context = useContext(ErrorDialogContext);
  if (context === undefined) {
    throw new Error('useErrorDialog must be used within an ErrorDialogProvider');
  }
  return context;
};
